import type { Catalog, Product, Bundle, SKU } from './canonical';

/**
 * Item du catalogue unifié (Product OU Bundle wrapped pour rendering).
 */
export interface CatalogItem {
  sku: SKU;
  kind: 'product' | 'bundle';
  /** F-series family code extracted from SKU. F01/F02/F03/F04/F05/F07, ou 'other'. */
  family: string;
  familyLabel: string;
  /** Description courte (descriptionFr from product or bundle) */
  label: string;
  /** Prix de vente CAD (sellCad pour product, 0 pour bundle car pas de pricing dans le schema) */
  priceCad: number;
  /** Catégorie originale (product.category ou 'bundle') */
  category: string;
  /** subCategory si dispo */
  subCategory?: string;
  /** dimensions string si dispo (ex: "48" × 48"") */
  dimensionsLabel?: string;
  /** URL image relative (ex: "/assets/parts/F01005.png") ou undefined si manifest ne contient pas */
  imageUrl?: string;
  // === Enrichments propagated from Product (PDF metadata / parts inventory) ===
  /** Weight in pounds. */
  weightLbs?: number;
  /** Weight in kilograms. */
  weightKg?: number;
  /** Material grade (e.g. "6005A-T61"). */
  material?: string;
  /** ISO date YYYY-MM-DD of the technical drawing. */
  drawingDate?: string;
  /** Revision number (0, 1, ...). */
  revision?: number;
  /** Drawing scale (e.g. "1:12"). */
  scale?: string;
  /** Drawn-by initials. */
  drawnBy?: string;
  /** Plate / extrusion thickness (in). */
  thicknessIn?: number;
  /** Round bar diameter (in). */
  diameterIn?: number;
  /** Référence raw vers Product ou Bundle pour détails complets */
  raw: Product | Bundle;
}

const FAMILY_LABELS: Record<string, string> = {
  F01: 'Plateforme',
  F02: 'Trottoir',
  F03: 'Garde-corps',
  F04: 'Patte',
  F05: 'Transition de sol',
  F07: 'Passe-seuil',
  other: 'Autres',
};

function extractFamily(skuStr: string): string {
  const match = skuStr.match(/^(F\d{2})\d{3}/);
  return match && match[1] ? match[1] : 'other';
}

function formatDimensions(p: Product): string | undefined {
  const d = p.dimensions;
  if (!d) return undefined;
  const parts: string[] = [];
  if (d.widthIn) parts.push(`${d.widthIn}"`);
  if (d.lengthIn) parts.push(`${d.lengthIn}"`);
  if (d.heightIn) parts.push(`${d.heightIn}"`);
  return parts.length > 0 ? parts.join(' × ') : undefined;
}

export interface GetAllSkusOptions {
  /** Mapping SKU → image URL relative path. Pass the `thumbnails` field of /assets/parts/manifest.json */
  imageManifest?: Record<string, string>;
}

/**
 * Retourne TOUS les items du catalogue (products + bundles fusionnés) triés par famille puis SKU.
 * Performance : O(n) sur le nombre d'items du catalogue (583 + 26 ≈ 609).
 */
export function getAllSkus(catalog: Catalog, opts: GetAllSkusOptions = {}): CatalogItem[] {
  const items: CatalogItem[] = [];
  const imageManifest = opts.imageManifest ?? {};

  for (const [skuKey, product] of catalog.products) {
    const skuStr = skuKey as unknown as string;
    const family = extractFamily(skuStr);
    const dims = formatDimensions(product);
    // Image priority: explicit manifest > product.imageUrl (baked at build time).
    const img = imageManifest[skuStr] ?? product.imageUrl;
    const item: CatalogItem = {
      sku: skuKey,
      kind: 'product',
      family,
      familyLabel: FAMILY_LABELS[family] ?? family,
      label: product.descriptionFr || product.descriptionEn || skuStr,
      priceCad: product.pricing?.sellCad ?? 0,
      category: product.category,
      raw: product,
    };
    if (product.subCategory) item.subCategory = product.subCategory;
    if (dims) item.dimensionsLabel = dims;
    if (img) item.imageUrl = img;
    // Propagate enrichments (exactOptionalPropertyTypes: only set when defined)
    if (product.weightLbs != null) item.weightLbs = product.weightLbs;
    if (product.weightKg != null) item.weightKg = product.weightKg;
    if (product.material) item.material = product.material;
    if (product.drawingDate) item.drawingDate = product.drawingDate;
    if (product.revision != null) item.revision = product.revision;
    if (product.scale) item.scale = product.scale;
    if (product.drawnBy) item.drawnBy = product.drawnBy;
    if (product.dimensions?.thicknessIn != null) {
      item.thicknessIn = product.dimensions.thicknessIn;
    }
    if (product.dimensions?.diameterIn != null) {
      item.diameterIn = product.dimensions.diameterIn;
    }
    items.push(item);
  }

  for (const [skuKey, bundle] of catalog.bundles) {
    const skuStr = skuKey as unknown as string;
    const family = extractFamily(skuStr);
    const img = imageManifest[skuStr];
    const item: CatalogItem = {
      sku: skuKey,
      kind: 'bundle',
      family,
      familyLabel: FAMILY_LABELS[family] ?? family,
      label: bundle.descriptionFr || skuStr,
      priceCad: 0,
      category: 'bundle',
      raw: bundle,
    };
    if (img) item.imageUrl = img;
    items.push(item);
  }

  // Sort by family (F01 < F02 ... < other) then by SKU
  const familyOrder = ['F01', 'F02', 'F03', 'F04', 'F05', 'F07', 'other'];
  items.sort((a, b) => {
    const ai = familyOrder.indexOf(a.family);
    const bi = familyOrder.indexOf(b.family);
    const aIdx = ai === -1 ? familyOrder.length : ai;
    const bIdx = bi === -1 ? familyOrder.length : bi;
    if (aIdx !== bIdx) return aIdx - bIdx;
    return (a.sku as unknown as string).localeCompare(b.sku as unknown as string);
  });

  return items;
}

/**
 * Group items by family. Convenience helper for UI.
 */
export function groupByFamily(items: CatalogItem[]): Map<string, CatalogItem[]> {
  const map = new Map<string, CatalogItem[]>();
  for (const item of items) {
    const arr = map.get(item.family) ?? [];
    arr.push(item);
    map.set(item.family, arr);
  }
  return map;
}

/**
 * Family code → human label.
 */
export const FAMILY_LABELS_EXPORT: Readonly<Record<string, string>> = FAMILY_LABELS;
